import {
  BooleanField,
  DateTimeField,
  NumberField,
  RelationshipField,
  RelationshipFieldMany,
  StringField,
  LongTextField,
} from "../plugins/Cruxtify/field-types";
import {
  BooleanProperty,
  DateTimeProperty,
  NumberProperty,
  RelationshipProperty,
  RelationshipPropertyMany,
  StringProperty,
} from "../plugins/Cruxtify/property-types";
import {
  UserPermission,
  UserPermissions,
  UserRole,
} from "../plugins/Cruxtify/UserPermission";
import { ApcCrudModel } from "./ApcCrudModel";

export class ConfigurableSlot extends ApcCrudModel {
  public static icon = "mdi-puzzle-outline";
  protected static _typeLabel = "Configurable Slot";
  protected static asProperty = "configurable_slot";
  public static api = {
    path: "configurable-slots",
  };

  protected static routeBase = "configurable-slots";

  protected static userPermissions = new UserPermissions(
    {
      [UserRole.SuperAdmin]: UserPermission.Edit,
      [UserRole.LoggedIn]: UserPermission.Read,
    },
    UserPermission.New
  );

  protected static propertyDefinitions = [
    {
      type: NumberProperty,
      opts: {
        name: "id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "created_at",
        label: "Created At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "updated_at",
        label: "Updated At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "deleted_at",
        label: "deleted At",
        userPermissions: UserPermission.Hidden,
        computed: true,
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "configurable_slot_type",
        label: "Slot Type",
        relatedModel: "ConfigurableSlotType",
        foreignProperty: "configurable_slots",
        rules: ["required"],
      },
    },
    {
      type: NumberProperty,
      opts: {
        name: "order",
        label: "Display Order",
        sortable: true,
        userPermissions: UserPermission.Read,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "description",
        label: "Description",
      },
    },
    {
      type: NumberProperty,
      opts: {
        name: "max_selections",
        label: "Maximum Selections",
        description: "Maximum number of options that can be selected",
      },
    },
    {
      type: NumberProperty,
      opts: {
        name: "min_selections",
        label: "Minimum Selections",
        description: "Minimum number of options that must be selected",
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "product",
        label: "Product",
        relatedModel: "Product",
        foreignProperty: "configurable_slots",
        rules: ["required"],
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "suggested_option",
        label: "Suggested Option",
        relatedModel: "Option",
        foreignProperty: "suggested_for_slots",
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "options",
        label: "Available Options",
        relatedModel: "Option",
        foreignProperty: "configurable_slots",
      },
    },
  ];

  protected static fieldDefinitions = [
    { type: NumberField, opts: { property: "id" } },
    { type: DateTimeField, opts: { property: "created_at" } },
    { type: DateTimeField, opts: { property: "updated_at" } },
    {
      type: RelationshipField,
      opts: { property: "configurable_slot_type", isPrimaryLabelField: 2 },
    },
    { type: NumberField, opts: { property: "order" } },
    { type: LongTextField, opts: { property: "description" } },
    { type: NumberField, opts: { property: "max_selections" } },
    { type: NumberField, opts: { property: "min_selections" } },
    {
      type: RelationshipField,
      opts: { property: "product", isPrimaryLabelField: 1 },
    },
    { type: RelationshipField, opts: { property: "suggested_option" } },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "options",
        viewComponentName: "form-relationship-field-many-table",
      },
    },
  ];

  protected static collectionLayoutDefinitions = [
    {
      id: "table",
      columnFields: [
        "configurable_slot_type",
        "order",
        "product",
        "min_selections",
        "max_selections",
      ],
      enableSearch: true,
      enableDynamicFilters: true,
      useModal: true,
      dynamicFilterOptions: {
        filterFields: [
          "configurable_slot_type",
          "order",
          "description",
          "product",
          "min_selections",
          "max_selections",
          "suggested_option",
        ],
      },
      defaultSort: [
        {
          field: "order",
          order: "asc",
        },
      ],
    },
    {
      id: "field",
      columnFields: [
        "configurable_slot_type",
        "min_selections",
        "max_selections",
      ],
      enableSearch: false,
      enableDynamicFilters: false,
      useModal: false,
      noRefresh: true,
      ordinalField: "order",
      expansionComponent: {
        name: "ModelLayoutExpansion",
        opts: {
          layoutId: "expansion",
        },
      },
    },
  ];

  protected static modelLayoutDefinitions = [
    {
      type: "FieldSetGridLayout",
      opts: {
        id: "slotDetails",
        fieldSets: [
          {
            id: "main",
            fields: [
              "configurable_slot_type",
              "description",
              "product",
              "min_selections",
              "max_selections",
              "suggested_option",
            ],
          },
          {
            id: "options",
            fields: ["options"],
          },
          {
            id: "meta",
            fields: ["created_at", "updated_at", "id", "order"],
          },
        ],
      },
    },
    {
      type: "FieldSetGridLayout",
      opts: {
        id: "expansion",
        fieldSets: [
          {
            id: "main",
            fields: [
              "configurable_slot_type",
              "description",
              "min_selections",
              "max_selections",
            ],
          },
          {
            id: "options",
            fields: ["options", "suggested_option"],
          },
        ],
      },
    },
  ];
}
